import React from 'react';
import {Box, Button, Checkbox, Chip, Dialog, DialogContent, DialogTitle, FormControlLabel, Tab, Tabs, TextField, Tooltip, Typography} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {Chart} from 'react-chartjs-2';
import {
    WeibullFit,
    weibullBLife,
    weibullCDF,
    weibullDiagnosis,
    weibullFitQuality,
    weibullPDF,
} from '../../utils/weibull';
import {CrowAmsaaFit, crowAmsaaDiagnosis, crowAmsaaForecast} from '../../utils/crowAmsaa';

/** Per-entity event-gap distribution, computed in the parent and passed in. */
export interface GapDistData {
    gaps: number[];
    bins: {start: number; end: number; count: number}[];
    cdfPoints: {x: number; y: number}[];
    stats: {n: number; mean: number; median: number; min: number; q1: number; q3: number; max: number};
}

interface TimeToFailureDialogProps {
    /** The entity whose event gaps to show; null closes the dialog. */
    entity: {key: string; label: string; sublabel?: string} | null;
    onClose: () => void;
    gapDistData: GapDistData | null;
    weibullFit: WeibullFit | null;
    showWeibullFit: boolean;
    onShowWeibullFitChange: (value: boolean) => void;
    predictionDays: number;
    onPredictionDaysChange: (value: number) => void;
    crowAmsaaFit: CrowAmsaaFit | null;
    initialTab: 'gap' | 'growth';
    /** Singular noun for the entity (e.g. 'barrier', 'cluster'). Defaults to 'barrier'. */
    entityNoun?: string;
    /** Singular noun for the recurring event (e.g. 'failure', 'occurrence'). Defaults to 'failure'. */
    eventNoun?: string;
    /** When provided, renders a "Show in {entityNoun} list" affordance in the title row. */
    onShowInList?: () => void;
}

/** Time-to-failure drill-down: empirical PDF (histogram) + CDF over the gaps between
 *  consecutive events, with an opt-in fitted-Weibull overlay (regime diagnosis, B-life,
 *  prediction) and a Crow-AMSAA reliability-growth tab. The gap stats and
 *  fits are memoised by the parent and supplied as props; this component is pure
 *  presentation. Generic over the entity (barrier, cluster, …) and the event noun. */
export const TimeToFailureDialog: React.FC<TimeToFailureDialogProps> = ({
    entity,
    onClose,
    gapDistData,
    weibullFit,
    showWeibullFit,
    onShowWeibullFitChange,
    predictionDays,
    onPredictionDaysChange,
    crowAmsaaFit,
    initialTab,
    entityNoun = 'barrier',
    eventNoun = 'failure',
    onShowInList,
}) => {
    const [tab, setTab] = React.useState<'gap' | 'growth'>('gap');

    // Re-apply the parent's auto-selected tab whenever the dialog opens or the
    // entity changes. Keyed on entity identity AND initialTab so opening a new
    // entity (or the parent re-deciding the lens) snaps to the right tab.
    React.useEffect((): void => {
        if (entity != null) setTab(initialTab);
    }, [entity?.key, initialTab]);

    return (
        <Dialog open={entity != null} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{pb: 1}}>
                <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1}}>
                    <Box sx={{flex: 1, minWidth: 0}}>
                        Time-to-failure: {entity?.label}
                        {entity?.sublabel && (
                            <Typography variant="caption" sx={{display: 'block', color: 'text.secondary', mt: 0.5}}>
                                {entity.sublabel}
                            </Typography>
                        )}
                    </Box>
                    {onShowInList && (
                        <Button size="small" onClick={onShowInList} sx={{flexShrink: 0, mt: 0.25}}>
                            Show in {entityNoun} list
                        </Button>
                    )}
                </Box>
            </DialogTitle>
            <Tabs value={tab} onChange={(_e, v): void => setTab(v)} sx={{px: 3, borderBottom: 1, borderColor: 'divider'}}>
                <Tab label="Gap distribution" value="gap" />
                <Tab label="Reliability growth" value="growth" />
            </Tabs>
            <DialogContent>
                {tab === 'gap' && ((): React.ReactNode => {
                    if (!gapDistData) {
                        return (
                            <Typography variant="body2" color="text.secondary">
                            Need at least 2 {eventNoun} events with valid dates to compute a distribution.
                            </Typography>
                        );
                    }
                    const {bins, cdfPoints, stats} = gapDistData;
                    const labels = bins.map((b) => `${Math.round(b.start)}–${Math.round(b.end)}d`);
                    const binWidth = bins.length > 0 ? bins[0].end - bins[0].start : 0;
                    const cdfAtBinEnd = bins.map((b) => {
                        const upTo = cdfPoints.filter((p) => p.x <= b.end).length;
                        return (upTo / stats.n) * 100;
                    });
                    // Weibull overlay datasets. PDF is scaled to expected-count
                    // (n × f(t) × binWidth) so it shares the histogram's Y axis.
                    // Sampled at bin centers; lines are smooth in absolute terms,
                    // categorical-x just means they're rendered at one point per bin.
                    const weibullPdfData = weibullFit
                        ? bins.map((b) => stats.n * weibullPDF((b.start + b.end) / 2, weibullFit.beta, weibullFit.eta) * binWidth)
                        : [];
                    const weibullCdfData = weibullFit
                        ? bins.map((b) => weibullCDF(b.end, weibullFit.beta, weibullFit.eta) * 100)
                        : [];
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const data: any = {
                        labels,
                        datasets: [
                            {
                                type: 'bar' as const,
                                label: 'Gap count (Probability Density Function)',
                                data: bins.map((b) => b.count),
                                backgroundColor: 'rgba(211, 47, 47, 0.6)',
                                borderColor: 'rgba(211, 47, 47, 1)',
                                borderWidth: 1,
                                yAxisID: 'yCount',
                                order: 3,
                            },
                            {
                                type: 'line' as const,
                                label: 'Cumulative % (Cumulative Distribution Function)',
                                data: cdfAtBinEnd,
                                borderColor: '#1976d2',
                                backgroundColor: '#1976d2',
                                borderWidth: 2,
                                pointRadius: 3,
                                tension: 0.2,
                                yAxisID: 'yPct',
                                order: 2,
                            },
                            ...(weibullFit ? [
                                {
                                    type: 'line' as const,
                                    label: 'Weibull PDF (fit)',
                                    data: weibullPdfData,
                                    borderColor: '#616161',
                                    backgroundColor: 'transparent',
                                    borderWidth: 1.5,
                                    borderDash: [6, 4],
                                    pointRadius: 0,
                                    tension: 0.3,
                                    yAxisID: 'yCount',
                                    order: 1,
                                },
                                {
                                    type: 'line' as const,
                                    label: 'Weibull CDF (fit)',
                                    data: weibullCdfData,
                                    borderColor: '#1976d2',
                                    backgroundColor: 'transparent',
                                    borderWidth: 1.5,
                                    borderDash: [6, 4],
                                    pointRadius: 0,
                                    tension: 0.3,
                                    yAxisID: 'yPct',
                                    order: 0,
                                },
                            ] : []),
                        ],
                    };
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const options: any = {
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: {mode: 'index' as const, intersect: false},
                        plugins: {
                            legend: {position: 'top' as const, labels: {boxWidth: 12, font: {size: 11}}},
                            tooltip: {
                                callbacks: {
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    label: (ctx: any): string => {
                                        const fitted = typeof ctx.dataset.label === 'string' && ctx.dataset.label.includes('fit');
                                        // Cumulative-% datasets (empirical CDF + Weibull CDF fit) live on the
                                        // yPct axis → read as "% of gaps ≤ Nd". Everything else is a COUNT on
                                        // the yCount axis (the bar + the Weibull PDF fit, an expected-count
                                        // curve) → read as gap counts. Without the axis check, the PDF-fit
                                        // count was mislabeled "382% of gaps ≤ 4d".
                                        if (ctx.dataset.yAxisID === 'yPct') {
                                            return `${ctx.parsed.y.toFixed(0)}% of gaps ≤ ${Math.round(bins[ctx.dataIndex].end)}d${fitted ? ' (fitted)' : ''}`;
                                        }
                                        if (fitted) return `~${Math.round(ctx.parsed.y)} gaps expected in ${ctx.label} (fitted)`;
                                        return `${ctx.parsed.y} gap${ctx.parsed.y === 1 ? '' : 's'} in ${ctx.label}`;
                                    },
                                },
                            },
                        },
                        scales: {
                            x: {title: {display: true, text: `Gap between consecutive ${eventNoun}s (days)`}, ticks: {font: {size: 10}}},
                            yCount: {
                                type: 'linear' as const,
                                position: 'left' as const,
                                title: {display: true, text: 'Gap count'},
                                beginAtZero: true,
                                grace: '5%',
                                ticks: {precision: 0},
                            },
                            yPct: {
                                type: 'linear' as const,
                                position: 'right' as const,
                                title: {display: true, text: 'Cumulative %'},
                                min: 0,
                                // max slightly above 100 so the CDF's 100% plateau clears the top
                                // edge; stepSize keeps the top labeled tick at 100.
                                max: 105,
                                ticks: {stepSize: 20},
                                grid: {drawOnChartArea: false},
                            },
                        },
                    };
                    return (
                        <>
                            {/* Summary stats */}
                            <Box sx={{display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap'}}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{textTransform: 'capitalize'}}>{eventNoun}s</Typography>
                                    <Typography variant="body2" fontWeight="bold">{stats.n + 1}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Gaps</Typography>
                                    <Typography variant="body2" fontWeight="bold">{stats.n}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Mean (MTBF)</Typography>
                                    <Typography variant="body2" fontWeight="bold">{Math.round(stats.mean)}d</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Min</Typography>
                                    <Typography variant="body2" fontWeight="bold">{Math.round(stats.min)}d</Typography>
                                </Box>
                                <Tooltip arrow title={`First quartile (25th percentile) — 25% of the ${eventNoun} gaps are at or below this many days`}>
                                    <Box sx={{cursor: 'help'}}>
                                        <Typography variant="caption" color="text.secondary">Q1</Typography>
                                        <Typography variant="body2" fontWeight="bold">{Math.round(stats.q1)}d</Typography>
                                    </Box>
                                </Tooltip>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Median</Typography>
                                    <Typography variant="body2" fontWeight="bold">{Math.round(stats.median)}d</Typography>
                                </Box>
                                <Tooltip arrow title={`Third quartile (75th percentile) — 75% of the ${eventNoun} gaps are at or below this many days`}>
                                    <Box sx={{cursor: 'help'}}>
                                        <Typography variant="caption" color="text.secondary">Q3</Typography>
                                        <Typography variant="body2" fontWeight="bold">{Math.round(stats.q3)}d</Typography>
                                    </Box>
                                </Tooltip>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Max</Typography>
                                    <Typography variant="body2" fontWeight="bold">{Math.round(stats.max)}d</Typography>
                                </Box>
                            </Box>

                            {/* Weibull overlay toggle + fit stats block */}
                            <Box sx={{display: 'flex', alignItems: 'center', mb: 1, gap: 0.5}}>
                                <Tooltip
                                    arrow
                                    title={`Overlay a fitted two-parameter Weibull distribution. Adds a regime diagnosis (Infant mortality / Random / Wear-out from the shape parameter β) and B-life predictions (B10/B50/B90). Use this when you want a model behind the empirical shape — to compare regimes across ${entityNoun}s or to forecast future ${eventNoun}s.`}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={showWeibullFit}
                                                disabled={stats.n < 3}
                                                onChange={(e): void => onShowWeibullFitChange(e.target.checked)}
                                                sx={{py: 0}}
                                            />
                                        }
                                        label={<Typography variant="body2">Overlay Weibull fit</Typography>}
                                        sx={{m: 0}}
                                    />
                                </Tooltip>
                                {stats.n < 3 && (
                                    <Typography variant="caption" sx={{color: 'text.disabled', fontStyle: 'italic', ml: 1}}>
                                    (needs ≥ 3 {eventNoun} gaps)
                                    </Typography>
                                )}
                            </Box>

                            {/* Cross-model note next to the overlay control: this whole tab —
                            fit quality AND the regime label — is the time-since-last-failure
                            clock, not a calendar-time trend. */}
                            {showWeibullFit && weibullFit && (
                                <Typography variant="caption" sx={{display: 'block', mb: 1.5, color: 'text.secondary', fontStyle: 'italic'}}>
                                The regime (infant mortality / wear-out) describes risk <em>vs time since the last failure</em> — e.g. infant mortality = risk falls the longer it stays quiet. It is <em>not</em> a calendar-time trend: for whether failures are getting more or less frequent over the years, see the Reliability growth tab.
                                </Typography>
                            )}

                            {showWeibullFit && weibullFit && ((): React.ReactNode => {
                                const dx = weibullDiagnosis(weibullFit.beta);
                                const fq = weibullFitQuality(weibullFit.r2);
                                const b10 = weibullBLife(0.1, weibullFit.beta, weibullFit.eta);
                                const b50 = weibullBLife(0.5, weibullFit.beta, weibullFit.eta);
                                const b90 = weibullBLife(0.9, weibullFit.beta, weibullFit.eta);
                                return (
                                    <Box sx={{mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, backgroundColor: 'background.default'}}>
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1}}>
                                            <Tooltip arrow title="Direction of risk vs time SINCE THE LAST FAILURE (within each gap) — the ↓/↑ is not a calendar trend. Infant mortality (β<1): risk falls the longer it stays quiet. Wear-out (β>1): risk rises. Random (β≈1): age doesn't matter. For a calendar-time trend, see the Reliability growth tab.">
                                                <Chip
                                                    label={`${dx.arrow} ${dx.label.toUpperCase()}`}
                                                    size="small"
                                                    sx={{height: 24, fontSize: '0.7rem', fontWeight: 'bold', backgroundColor: dx.bgColor, color: dx.color, cursor: 'help'}}
                                                />
                                            </Tooltip>
                                            <Typography variant="caption" sx={{color: 'text.disabled', fontStyle: 'italic'}}>vs time since last failure</Typography>
                                            <Typography variant="caption" sx={{color: 'text.secondary'}}>
                                            β (shape) = <strong>{weibullFit.beta.toFixed(2)}</strong> ± {weibullFit.betaSE.toFixed(2)}
                                            </Typography>
                                            <Typography variant="caption" sx={{color: 'text.secondary'}}>
                                            η (scale) = <strong>{Math.round(weibullFit.eta)}d</strong> ± {Math.round(weibullFit.etaSE)}d
                                                <span style={{color: '#999', marginLeft: 4}}>(characteristic life)</span>
                                            </Typography>
                                        </Box>
                                        <Box sx={{display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center'}}>
                                            <Tooltip arrow title={`10% of failure gaps are ≤ ${Math.round(b10)} days — i.e. 1 in 10 times the next ${eventNoun} comes within ${Math.round(b10)} day(s) of the last. (Gap length, not calendar time.)`}>
                                                <Box sx={{cursor: 'help'}}>
                                                    <Typography variant="caption" color="text.secondary">B10</Typography>
                                                    <Typography variant="body2" fontWeight="bold">{Math.round(b10)}d</Typography>
                                                    <Typography variant="caption" sx={{display: 'block', color: 'text.disabled', fontSize: '0.65rem'}}>10 % of gaps ≤ this</Typography>
                                                </Box>
                                            </Tooltip>
                                            <Tooltip arrow title={`Median gap: half of failure gaps are ≤ ${Math.round(b50)} days and half are longer — the typical time between ${eventNoun}s (more honest than the mean when the spread is skewed).`}>
                                                <Box sx={{cursor: 'help'}}>
                                                    <Typography variant="caption" color="text.secondary">B50</Typography>
                                                    <Typography variant="body2" fontWeight="bold">{Math.round(b50)}d</Typography>
                                                    <Typography variant="caption" sx={{display: 'block', color: 'text.disabled', fontSize: '0.65rem'}}>median gap</Typography>
                                                </Box>
                                            </Tooltip>
                                            <Tooltip arrow title={`90% of failure gaps are ≤ ${Math.round(b90)} days; only 10% run longer. Stay failure-free past ${Math.round(b90)} days and you're in the longest 10% of intervals.`}>
                                                <Box sx={{cursor: 'help'}}>
                                                    <Typography variant="caption" color="text.secondary">B90</Typography>
                                                    <Typography variant="body2" fontWeight="bold">{Math.round(b90)}d</Typography>
                                                    <Typography variant="caption" sx={{display: 'block', color: 'text.disabled', fontSize: '0.65rem'}}>90 % of gaps ≤ this</Typography>
                                                </Box>
                                            </Tooltip>
                                            <Box sx={{ml: 'auto'}}>
                                                <Typography variant="caption" color="text.secondary">Fit quality</Typography>
                                                <Typography variant="body2" fontWeight="bold" sx={{color: fq.color}}>
                                                    {fq.symbol} {fq.label}
                                                </Typography>
                                                <Typography variant="caption" sx={{display: 'block', color: 'text.disabled', fontSize: '0.65rem'}}>
                                                R² (Weibull plot) = {weibullFit.r2.toFixed(3)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        {/* Interactive prediction — analyst types a time horizon and reads off
                                         P(event within t) directly from the fitted Weibull CDF. */}
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap'}}>
                                            <Typography variant="caption" color="text.secondary">
                                            Predict probability of {eventNoun} within
                                            </Typography>
                                            <TextField
                                                size="small"
                                                type="number"
                                                value={predictionDays}
                                                onChange={(e): void => {
                                                    const v = Number(e.target.value);
                                                    onPredictionDaysChange(isNaN(v) || v < 0 ? 0 : v);
                                                }}
                                                slotProps={{
                                                    htmlInput: {
                                                        min: 0, step: 1, style: {textAlign: 'right', padding: '2px 6px'}
                                                    }
                                                }}
                                                sx={{width: 80, '& .MuiInputBase-input': {fontSize: '0.8rem'}}}
                                            />
                                            <Typography variant="caption" color="text.secondary">
                                            day{predictionDays === 1 ? '' : 's'}:
                                            </Typography>
                                            <Typography variant="body2" fontWeight="bold" sx={{color: 'primary.main', ml: 0.5}}>
                                                {(weibullCDF(predictionDays, weibullFit.beta, weibullFit.eta) * 100).toFixed(1)}%
                                            </Typography>
                                            <Tooltip
                                                arrow
                                                title={`Marginal probability that the next ${eventNoun}-gap is ≤ N days, computed from the fitted Weibull CDF: F(N) = 1 − exp(−(N/η)^β). Useful for short-horizon planning: 'what's the chance we see another ${eventNoun} in the next 30 days?'`}
                                            >
                                                <HelpOutlineIcon sx={{fontSize: 13, color: 'text.disabled', cursor: 'help'}} />
                                            </Tooltip>
                                        </Box>
                                        {stats.n < 10 && (
                                            <Typography variant="caption" sx={{display: 'block', mt: 1, color: '#f57c00'}}>
                                            ⚠ n = {stats.n}. Fit is informative but uncertain — β confidence interval is wide. Stable Weibull fits typically need ≥ 10 {eventNoun} gaps; below 5, ignore β entirely.
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })()}

                            {showWeibullFit && !weibullFit && stats.n >= 3 && (
                                <Typography variant="caption" sx={{display: 'block', mb: 1, color: '#d32f2f'}}>
                                Weibull fit did not converge. The gap data may not be Weibull-distributed (e.g. multi-modal {eventNoun}s), or all gaps may be identical.
                                </Typography>
                            )}

                            <Box sx={{height: 320}}>
                                <Chart type="bar" data={data} options={options} />
                            </Box>
                            <Typography variant="caption" sx={{display: 'block', mt: 1, color: 'text.secondary', fontStyle: 'italic'}}>
                            Bars (red) show the count of inter-{eventNoun} gaps falling into each duration bin — the empirical Probability Density Function.
                            The solid blue line shows the empirical Cumulative Distribution Function.
                                {showWeibullFit && weibullFit && ' Dashed lines are the fitted Weibull PDF (gray) and CDF (blue) — if they hug the empirical curves, the Weibull model is a good description of the process.'}
                            </Typography>
                        </>
                    );
                })()}
                {tab === 'growth' && ((): React.ReactNode => {
                    if (crowAmsaaFit == null) {
                        return (
                            <Typography variant="body2" color="text.secondary">
                            Need at least 8 dated {eventNoun}s to fit a reliability-growth model.
                            </Typography>
                        );
                    }
                    const {beta, lambda, betaLower, betaUpper, r2, n, T, endMs, points} = crowAmsaaFit;
                    const anchorDate = new Date(endMs).toLocaleDateString('en-US', {year: 'numeric', month: 'short', day: 'numeric'});
                    const dx = crowAmsaaDiagnosis(beta);
                    const trendPresent = beta > 1.1 || beta < 0.9;

                    // Empirical cumulative-event points (log-log scatter).
                    const empirical = points.map((p) => ({x: p.t, y: p.cum}));
                    // Fitted power-law curve E[N(t)] = lambda · t^beta, sampled across the
                    // observed x-range. Log axes can't show t = 0, so start at the first
                    // empirical point (t > 0 by construction) and extend to the truncation T.
                    const tStart = points.length > 0 ? points[0].t : 0.01;
                    const tEnd = Math.max(tStart * 1.0001, T);
                    const fitSteps = 40;
                    const fitted: {x: number; y: number}[] = [];
                    for (let i = 0; i <= fitSteps; i++) {
                        const x = tStart + (i / fitSteps) * (tEnd - tStart);
                        fitted.push({x, y: lambda * Math.pow(x, beta)});
                    }
                    // Forecast numbers for the card below. (No on-chart dashed extension: a
                    // days-long horizon beyond a multi-year, log-scaled observation is a
                    // sub-pixel nub at the right edge — invisible and misleading as a legend
                    // entry. The forecast is conveyed by the card instead.)
                    const forecast = crowAmsaaForecast(crowAmsaaFit, predictionDays);

                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const data: any = {
                        datasets: [
                            {
                                type: 'scatter' as const,
                                label: `Cumulative ${eventNoun}s (empirical)`,
                                data: empirical,
                                borderColor: '#1976d2',
                                backgroundColor: '#1976d2',
                                pointRadius: 3,
                                showLine: true,
                                stepped: true,
                                order: 1,
                            },
                            {
                                type: 'line' as const,
                                label: 'Crow-AMSAA fit (λ·tᵝ)',
                                data: fitted,
                                borderColor: '#d32f2f',
                                backgroundColor: 'transparent',
                                borderWidth: 1.5,
                                pointRadius: 0,
                                order: 0,
                            },
                        ],
                    };
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const options: any = {
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: {mode: 'nearest' as const, intersect: false},
                        plugins: {
                            legend: {position: 'top' as const, labels: {boxWidth: 12, font: {size: 11}}},
                            tooltip: {
                                callbacks: {
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    label: (ctx: any): string => `N = ${ctx.parsed.y.toFixed(ctx.datasetIndex === 0 ? 0 : 1)} at t = ${ctx.parsed.x.toFixed(1)}d`,
                                },
                            },
                        },
                        scales: {
                            x: {type: 'logarithmic' as const, title: {display: true, text: 'Time in service (days)'}, ticks: {font: {size: 10}}},
                            y: {type: 'logarithmic' as const, title: {display: true, text: `Cumulative ${eventNoun}s`}, ticks: {font: {size: 10}}},
                        },
                    };
                    const ciHalf = (betaUpper - betaLower) / 2;
                    return (
                        <>
                            <Typography variant="caption" sx={{display: 'block', mb: 2, color: 'text.secondary'}}>
                            Cumulative {eventNoun}s over time on log-log axes. A straight line indicates a power-law trend; the slope β is the growth rate.
                            </Typography>
                            {/* Summary stats */}
                            <Box sx={{display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap', alignItems: 'center'}}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">β (slope)</Typography>
                                    <Typography variant="body2" fontWeight="bold">{beta.toFixed(2)} ± {ciHalf.toFixed(2)}</Typography>
                                    <Typography variant="caption" sx={{display: 'block', color: 'text.disabled', fontSize: '0.65rem'}}>
                                    95% CI [{betaLower.toFixed(2)}, {betaUpper.toFixed(2)}]
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{textTransform: 'capitalize'}}>{eventNoun}s</Typography>
                                    <Typography variant="body2" fontWeight="bold">{n}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">R² (log-log fit)</Typography>
                                    <Typography variant="body2" fontWeight="bold">{r2.toFixed(3)}</Typography>
                                </Box>
                                <Chip
                                    label={dx.label.toUpperCase()}
                                    size="small"
                                    sx={{height: 24, fontSize: '0.7rem', fontWeight: 'bold', backgroundColor: dx.bgColor, color: dx.color}}
                                />
                            </Box>
                            {/* Independent of the forecast horizon — derived from β, λ, T only. */}
                            <Box sx={{display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5, flexWrap: 'wrap'}}>
                                <Typography variant="caption" color="text.secondary">Expected time to next {eventNoun}:</Typography>
                                <Typography variant="body2" fontWeight="bold" sx={{color: 'primary.main'}}>
                                ~{Math.round(forecast.daysToNextFailure)}d
                                </Typography>
                            </Box>
                            <Box sx={{height: 280}}>
                                <Chart type="line" data={data} options={options} />
                            </Box>
                            {/* Forecast — extrapolate the fitted NHPP forward by a chosen horizon. */}
                            <Box sx={{mt: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, backgroundColor: 'background.default'}}>
                                {/* Horizon control + its dependent output on one line. */}
                                <Box sx={{display: 'flex', alignItems: 'baseline', gap: 1, mb: 1, flexWrap: 'wrap'}}>
                                    <Typography variant="caption" color="text.secondary">Forecast the next</Typography>
                                    <TextField
                                        size="small"
                                        type="number"
                                        value={predictionDays}
                                        onChange={(e): void => {
                                            const v = Number(e.target.value);
                                            onPredictionDaysChange(isNaN(v) || v < 0 ? 0 : v);
                                        }}
                                        slotProps={{htmlInput: {min: 0, step: 1, style: {textAlign: 'right', padding: '2px 6px'}}}}
                                        sx={{width: 80, '& .MuiInputBase-input': {fontSize: '0.8rem'}}}
                                    />
                                    <Typography variant="caption" color="text.secondary">day{predictionDays === 1 ? '' : 's'}:</Typography>
                                    <Tooltip arrow title={`Model-average number of ADDITIONAL ${eventNoun}s expected over the horizon (a statistical mean, not a guaranteed count). The actual number will vary around it.`}>
                                        <Typography variant="caption" color="text.secondary" sx={{cursor: 'help', ml: 0.5}}>avg. additional {eventNoun}s</Typography>
                                    </Tooltip>
                                    <Typography variant="body2" fontWeight="bold" sx={{color: 'primary.main'}}>
                                        {forecast.expectedFailures.toFixed(1)}
                                    </Typography>
                                    <Typography variant="caption" sx={{color: 'text.disabled', fontSize: '0.65rem'}}>
                                    (β-range {forecast.lowFailures.toFixed(1)}–{forecast.highFailures.toFixed(1)})
                                    </Typography>
                                </Box>
                                <Typography variant="caption" sx={{display: 'block', color: 'text.disabled', fontSize: '0.65rem'}}>
                                from {anchorDate} (end of observation)
                                </Typography>
                                <Typography variant="caption" sx={{display: 'block', mt: 1, color: 'text.disabled', fontStyle: 'italic'}}>
                                Extrapolation assumes the fitted trend continues; treat as indicative, not a guarantee.
                                </Typography>
                            </Box>
                            <Typography variant="caption" sx={{display: 'block', mt: 1, color: 'text.secondary', fontStyle: 'italic'}}>
                                {trendPresent
                                    ? 'A time trend is present, so this view best answers trend questions. The Gap distribution can still describe the gap shape, but its i.i.d.-gap assumption may understate the trend — read the two views together.'
                                    : `No strong time trend — the Gap distribution (Weibull) view describes the ${eventNoun} timing here just as well.`}
                            </Typography>
                        </>
                    );
                })()}
            </DialogContent>
        </Dialog>
    );
};
